import moment from "moment";
import PropTypes from "prop-types";
import debounce from "lodash/debounce";
import { useSnackbar } from "notistack";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import AppBar from "@mui/material/AppBar";
import Tooltip from "@mui/material/Tooltip";
import Toolbar from "@mui/material/Toolbar";
import TableRow from "@mui/material/TableRow";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import TextField from "@mui/material/TextField";
import { withStyles } from "@mui/styles";
import IconButton from "@mui/material/IconButton";
import React, { useEffect, useState, useRef, useCallback } from "react";
import TableContainer from "@mui/material/TableContainer";

import { styles } from "../../styles/global";
import { isAdmin } from "../../helpers/roles";
import { getCachedUsers } from "../../api/user";
import { Dialog } from "../BaseComponents/Dialog";
import { NoResults } from "../BaseComponents/NoResults";
import { AppSkeleton } from "../BaseComponents/Skeleton";
import BaseAvatarUser from "../BaseComponents/BaseAvatarUser";
import { copyText, filterFullArray, truncate } from "../../util";
import { deleteToken, getTokens, insertNewToken } from "../../api/token";
import { ActionCell, ClickToCopy, StyledTableCell, StyledTableRow } from "./styles";

function TokensTable(props) {
  const { classes, organizationId } = props;

  const [cachedUsers, setCachedUsers] = useState(null);
  const [rows, setRows] = React.useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [openTokenDialog, setOpenDialog] = useState(false);
  const [selectedToken, setSelectedToken] = useState(null);
  const [projectId, setProjectId] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isEmpty, setIsEmpty] = useState(false);
  const isUserAdmin = isAdmin();

  const generateTokenHandler = () => {
    generateNewToken(projectId);
    toggleNewTokenModal(false);
    setProjectId("");
  };

  const { enqueueSnackbar } = useSnackbar();

  const searchRef = useRef();

  const fetchTokens = useCallback(async () => {
    const tokens = await getTokens({ organizationId });
    setRows(tokens);
    setFilteredRows(tokens);
    setIsLoading(false);
    setIsEmpty(!tokens.length);
  }, [organizationId]);

  const generateNewToken = (projectId) => {
    insertNewToken({ projectId, organizationId })
      .then(() => {
        enqueueSnackbar("Token Generated");
        setIsLoading(true);
        fetchTokens();
        searchRef.current.value = "";
      })
      .catch((error) => {
        enqueueSnackbar("Something went wrong", { variant: "error" });
        console.error(error);
      });
  };

  useEffect(() => {
    setIsLoading(true);
    fetchTokens();
  }, [fetchTokens]);

  useEffect(() => {
    async function fetchCachedUsers() {
      const cachedUsersLS = await getCachedUsers({ organizationId });
      if (cachedUsersLS) {
        setCachedUsers(cachedUsersLS);
      }
    }
    fetchCachedUsers();
  }, [organizationId]);

  const toggleNewTokenModal = (isOpened) => {
    setOpenDialog(isOpened);
  };

  const handleDeleteToken = () => {
    deleteToken({ jwt: selectedToken, organizationId }).then(() => {
      setSelectedToken(null);
      enqueueSnackbar("Token deleted successfully.");
      fetchTokens();
    });
  };

  const handleClipboardCopy = (jwt) => {
    copyText(jwt);
    enqueueSnackbar("Copied to clickboard");
  };

  const onQueryChange = debounce((query) => {
    const newRows = filterFullArray(query, rows);
    setFilteredRows(newRows);
    if (!newRows.length) {
      setIsEmpty(true);
    } else if (isEmpty && newRows.length) {
      setIsEmpty(false);
    }
  }, 200);

  const deleteButtonProps = {
    name: "Delete",
    onAction: handleDeleteToken
  };

  const generateButtonProps = {
    name: "Generate",
    onAction: generateTokenHandler,
    disabled: !projectId
  };

  const onCancel = () => {
    setProjectId("");
    setSelectedToken(null);
    toggleNewTokenModal(false);
  };

  const skeletonWidth = isUserAdmin ? [90, 158, 168, 164, 174, 132] : [112, 192, 202, 200, 214];

  return (
    <>
      <Paper className={classes.paper}>
        <AppBar className={classes.searchBar} position="sticky" color="default" elevation={0}>
          <Toolbar>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <SearchIcon className={classes.block} color="inherit" />
              </Grid>
              <Grid item xs>
                <TextField
                  inputRef={searchRef}
                  fullWidth
                  placeholder="Filter tokens by project"
                  InputProps={{
                    className: classes.searchInput
                  }}
                  onKeyUp={(e) => onQueryChange(e.target.value)}
                />
              </Grid>
              <Grid item>
                <Button
                  disabled={!isUserAdmin}
                  variant="contained"
                  color="primary"
                  className={classes.addUser}
                  onClick={() => toggleNewTokenModal(true)}
                  startIcon={<AddIcon />}
                >
                  Generate New Token
                </Button>
              </Grid>
            </Grid>
          </Toolbar>
        </AppBar>
        <div className={classes.contentWrapper}>
          <TableContainer>
            <Table aria-label="customized table" className={classes.table}>
              <TableHead>
                <TableRow>
                  <StyledTableCell align="center">JWT</StyledTableCell>
                  <StyledTableCell align="left">Project ID</StyledTableCell>
                  <StyledTableCell align="center">Created At</StyledTableCell>
                  <StyledTableCell align="center">Expires At</StyledTableCell>
                  <StyledTableCell align="left">Created By</StyledTableCell>
                  {isUserAdmin && <StyledTableCell align="right">Actions</StyledTableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {!isEmpty &&
                  !isLoading &&
                  filteredRows.map((row, i) => {
                    const userObj = cachedUsers?.[row.creatorId];
                    return (
                      <StyledTableRow key={row.jwt + i}>
                        <StyledTableCell component="th" scope="row">
                          <ClickToCopy onClick={() => handleClipboardCopy(row.jwt)}>
                            {truncate(row.jwt, 6, true)}
                          </ClickToCopy>
                        </StyledTableCell>
                        <StyledTableCell align="left">{row.projectId}</StyledTableCell>
                        <StyledTableCell align="center">
                          {moment(row.createdAt).format("MM/DD/YYYY") || "-"}
                        </StyledTableCell>

                        <StyledTableCell align="center">
                          {moment(row.expireAt).format("MM/DD/YYYY") || "-"}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          {userObj ? <BaseAvatarUser user={userObj} /> : row.creatorId || "-"}
                        </StyledTableCell>
                        {isUserAdmin && (
                          <ActionCell align="center">
                            <Tooltip title="Delete">
                              <IconButton aria-label="delete" onClick={() => setSelectedToken(row.jwt)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </ActionCell>
                        )}
                      </StyledTableRow>
                    );
                  })}
                {isLoading && <AppSkeleton widths={skeletonWidth} />}
              </TableBody>
            </Table>
            {isEmpty && !isLoading && <NoResults />}
          </TableContainer>
        </div>
      </Paper>

      <Dialog
        title="Generate New Token"
        onCancel={onCancel}
        isOpened={openTokenDialog}
        buttonProps={generateButtonProps}
      >
        <TextField
          variant="outlined"
          autoFocus
          margin="dense"
          id="name"
          label="Project Id"
          type="text"
          fullWidth
          onChange={(e) => setProjectId(e.target.value)}
          value={projectId}
        />
      </Dialog>
      <Dialog title="Delete JWT?" isOpened={!!selectedToken} buttonProps={deleteButtonProps} onCancel={onCancel}>
        {truncate(selectedToken, 6, true)}
      </Dialog>
    </>
  );
}

TokensTable.propTypes = {
  classes: PropTypes.object.isRequired
};

export default withStyles(styles)(TokensTable);
